/**
 * Section Component
 * Reusable card-based section with consistent styling
 * Replaces repeated Card components across pages
 */

import React from 'react';
import { Card } from 'antd';
import { spacing, borderRadius, shadows, typography, colors } from '../../theme/designTokens';

/**
 * Section Component
 *
 * @param {string} title - Section title
 * @param {ReactNode} extra - Extra content (actions, buttons) for header
 * @param {ReactNode} children - Section content
 * @param {string} variant - Card variant (default, bordered, elevated, compact)
 * @param {boolean} loading - Show loading state
 * @param {boolean} collapsible - Make section collapsible
 * @param {boolean} defaultCollapsed - Default collapsed state
 * @param {object} style - Additional styles
 * @param {object} bodyStyle - Body-specific styles
 */
function Section({
  title,
  extra,
  children,
  variant = 'default',
  loading = false,
  collapsible = false,
  defaultCollapsed = false,
  style = {},
  bodyStyle = {},
  ...restProps
}) {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);

  // Variant styles
  const variantStyles = {
    default: {
      borderRadius: borderRadius.md,
      boxShadow: shadows.xs,
      border: `1px solid ${colors.border.light}`,
      marginBottom: spacing.lg,
    },
    bordered: {
      borderRadius: borderRadius.md,
      boxShadow: 'none',
      border: `1px solid ${colors.border.light}`,
      marginBottom: spacing.lg,
    },
    elevated: {
      borderRadius: borderRadius.md,
      boxShadow: shadows.sm,
      border: `1px solid ${colors.border.light}`,
      marginBottom: spacing.lg,
    },
    compact: {
      borderRadius: borderRadius.sm,
      boxShadow: shadows.xs,
      border: `1px solid ${colors.border.light}`,
      marginBottom: spacing.md,
    },
    flat: {
      borderRadius: borderRadius.md,
      boxShadow: 'none',
      border: `1px solid ${colors.border.light}`,
      marginBottom: spacing.lg,
    },
  };

  const currentVariantStyle = variantStyles[variant] || variantStyles.default;

  // Title with collapsible toggle
  const titleNode = collapsible && title ? (
    <div
      onClick={() => setCollapsed(!collapsed)}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: spacing.xs,
      }}
    >
      <span style={{ fontSize: typography.fontSize.sm }}>
        {collapsed ? '▶' : '▼'}
      </span>
      {title}
    </div>
  ) : (
    title
  );

  return (
    <Card
      title={titleNode}
      extra={extra}
      loading={loading}
      bordered={variant === 'bordered' || variant === 'flat'}
      style={{
        ...currentVariantStyle,
        ...style,
      }}
      bodyStyle={{
        display: collapsible && collapsed ? 'none' : 'block',
        padding: variant === 'compact' ? spacing.md : spacing.lg,
        ...bodyStyle,
      }}
      headStyle={{
        fontSize: typography.fontSize.md,
        fontWeight: typography.fontWeight.semibold,
        borderBottom: `1px solid ${colors.border.light}`,
      }}
      {...restProps}
    >
      {children}
    </Card>
  );
}

export default Section;
